import { toArray, isUndefined } from "lodash";
import { isBrowser } from "~/utilities/helpers";
import { breakpointNumbers } from "~/theme";

//#region Initialization and Targeting

export const initializeAccessibility = ({
    navRef,
    setAccessibilityInitialized,
}) => {
    if (isBrowser) {
        if (navRef.parent.current && navRef.parent.current[-1]) {
            const targetLabel = navRef.parent.current[-1].dataset.label;
            const targetLabelSliced = targetLabel
                .toString()
                .slice(0, targetLabel.length - 4);
            const links = toArray(
                document.querySelectorAll(
                    `[data-label="${targetLabelSliced}"]`,
                ),
            );
            navRef.children.current = links;
            if (navRef.children.current.length) {
                setAccessibilityInitialized(true);
            }
        }
    }
};

export const checkNavigationTarget = ({ keyboardEvent, navRef }) => {
    const isTarget =
        isBrowser && keyboardEvent.target === navRef.parent.current[-1];
    return isTarget;
};

export const checkTopHeaderTarget = (target, ref) => {
    if (isBrowser) {
        const checkChildNodes = (ref) => {
            if (ref.current.childNodes?.length) {
                return Array.from(ref.current.childNodes).find((child) =>
                    child.isEqualNode(target),
                );
            }

            return null;
        };

        const isTopNavLink = checkChildNodes(ref);

        return !!isTopNavLink;
    }
};

const targetCheck = (currentTarget, intendedTarget) => {
    const isTarget =
        !isUndefined(currentTarget) &&
        !isUndefined(intendedTarget) &&
        currentTarget === intendedTarget;
    return isTarget;
};

const documentQuerySingle = (queryString, operator) => {
    let operation = "=";
    if (operator) {
        operation = operator;
    }
    const query = `[data-label${operation}"${queryString}"]`;
    const queryResult = document.querySelector(query);
    return queryResult;
};

//#endregion

//#region Focus Management

const menuFocusManagement = ({ keyboardEvent, navRef }) => {
    if (navRef.children.current.length) {
        const navChildrenLength = navRef.children.current.length - 1;
        const firstElement = navRef.children.current[0];
        const lastElement = navRef.children.current[navChildrenLength];
        if (
            targetCheck(keyboardEvent.target, firstElement) &&
            keyboardEvent.shiftKey
        ) {
            keyboardEvent.preventDefault();
            lastElement.focus();
        } else if (
            targetCheck(keyboardEvent.target, lastElement) &&
            !keyboardEvent.shiftKey
        ) {
            keyboardEvent.preventDefault();
            firstElement.focus();
        }
    }
};

export const navCloseFocusManagement = ({ keyboardEvent }) => {
    if (isBrowser) {
        const topTarget = documentQuerySingle(
            `${keyboardEvent.target.dataset.label}-Top`,
        );
        if (topTarget) {
            topTarget.focus();
        }
    }
};

export const mobileMenuFocusManagement = ({
    mobileSubNavIsOpen,
    closingSubnav,
    mobileNavRef,
    setClosingSubnav,
}) => {
    if (isBrowser) {
        if (closingSubnav) {
            const target = documentQuerySingle(
                `${closingSubnav.split("-")[0]}-Mobile-Top`,
            );
            if (target) {
                target.focus();
                setClosingSubnav(undefined);
            }
        } else if (mobileSubNavIsOpen && mobileNavRef?.parent.current[-1]) {
            mobileNavRef.parent.current[-1].focus();
        }
    }
};

export const mobileNavOpenFocusManagement = ({
    focusedElement,
    setFocusedElement,
}) => {
    if (isBrowser) {
        const closeButton = documentQuerySingle("mobileMenuClose");
        if (closeButton) {
            closeButton.focus();
            if (focusedElement !== closeButton) {
                setFocusedElement(null);
            } else {
                setFocusedElement(closeButton);
            }
        }
    }
};

export const mobileNavCloseFocusManagement = () => {
    if (isBrowser) {
        const openButton = documentQuerySingle("mobileMenuToggle");
        if (openButton) {
            openButton.focus();
        }
    }
};

//#endregion

//#region Keyboard Navigation

export const keyboardMenuNavigation = ({ keyboardEvent, navRef }) => {
    if (isBrowser) {
        if (navRef.parent.current[-1]) {
            const targetLabel = navRef.parent.current[-1].dataset.label;
            const targetLabelSliced = targetLabel
                .toString()
                .slice(0, targetLabel.length - 4);
            const links = toArray(
                document.querySelectorAll(
                    `[data-label="${targetLabelSliced}"]`,
                ),
            );
            if (links.includes(keyboardEvent.target)) {
                navRef.children.current = links;
                menuFocusManagement({ keyboardEvent, navRef });
            }
        }
    }
};

export const mobileMenuKeyboardNavigation = ({
    keyboardEvent,
    mobileNavRef,
}) => {
    if (isBrowser && keyboardEvent.target.dataset.label) {
        if (
            keyboardEvent.shiftKey &&
            keyboardEvent.target.dataset.label.includes("mobileSubNavClose")
        ) {
            const dataLabel = mobileNavRef.parent.current[-1].dataset.label;
            const targetLabel = dataLabel.split("-")[0] + "-Last";
            const lastTarget = documentQuerySingle(targetLabel);

            if (lastTarget) {
                keyboardEvent.preventDefault();
                lastTarget.focus();
            }
        } else if (
            !keyboardEvent.shiftKey &&
            keyboardEvent.target.dataset.label.includes("Last")
        ) {
            if (breakpointNumbers.mobileLandscape <= window.innerWidth) {
                keyboardEvent.preventDefault();
                mobileNavRef.parent.current[-1].focus();
            }
        } else if (keyboardEvent.target.dataset.label.includes("-Mobile-Top")) {
            const tops = document.querySelectorAll(
                '[data-label*="-Mobile-Top"]',
            );
            if (tops.length) {
                const lastTop = tops[tops.length - 1];
                if (
                    targetCheck(keyboardEvent.target, lastTop) &&
                    !keyboardEvent.shiftKey
                ) {
                    keyboardEvent.preventDefault();
                    tops[0].focus();
                }
            }
        } else if (
            keyboardEvent.target.dataset.label.includes("mobileMenuClose") &&
            keyboardEvent.shiftKey
        ) {
            const tops = document.querySelectorAll(
                '[data-label*="-Mobile-Top"]',
            );
            if (tops.length) {
                const lastTop = tops[tops.length - 1];
                if (lastTop) {
                    keyboardEvent.preventDefault();
                    lastTop.focus();
                }
            }
        }
    }
};

export const mobileSecondaryNavKeyboardNavigation = ({
    keyboardEvent,
    mobileSubNavIsOpen,
}) => {
    if (isBrowser) {
        const mobileSecondaryNavLastLink =
            document.getElementById("mobileSecondaryNav").lastElementChild;
        const mobileSubNavClose = documentQuerySingle("mobileSubNavClose");
        const mobileMenuClose = documentQuerySingle("mobileMenuClose");
        const mobileSubNavFirstLink = documentQuerySingle("-Mobile-Top", "*=");
        if (
            targetCheck(keyboardEvent.target, mobileSecondaryNavLastLink) &&
            !keyboardEvent.shiftKey
        ) {
            keyboardEvent.preventDefault();
            if (mobileSubNavIsOpen.length) {
                mobileSubNavClose.focus();
            } else {
                mobileSubNavFirstLink.focus();
            }
        } else if (keyboardEvent.shiftKey) {
            if (targetCheck(keyboardEvent.target, mobileSubNavClose)) {
                keyboardEvent.preventDefault();
                mobileSecondaryNavLastLink.focus();
            } else if (
                mobileSubNavIsOpen.length === 0 &&
                targetCheck(keyboardEvent.target, mobileMenuClose)
            ) {
                keyboardEvent.preventDefault();
                mobileSecondaryNavLastLink.focus();
            }
        }
    }
};

export const topNavSubNavKeyboardNavigation = (
    keyboardEvent,
    topNavRef,
    hasSubitems,
    openTopSubnav,
    target,
    dispatch,
) => {
    const isTopHeaderTarget = checkTopHeaderTarget(
        keyboardEvent.target,
        topNavRef,
    );

    if (isTopHeaderTarget && hasSubitems) {
        keyboardEvent.preventDefault();
        if (openTopSubnav === target) {
            keyboardEvent.target.click();
        } else {
            dispatch({
                type: "HANDLE_MOUSE_ENTER_TOP",
                id: target,
                hasSubitems,
            });
        }
    }
};
//#endregion
